import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Clock, Sparkles, Upload, RefreshCw } from "lucide-react";
import { RuptureCard } from "./RuptureCard";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Section A de la Veille Proactive — Alertes ruptures personnalisées.
 *
 * Branchée sur GET /api/veille-proactive/ruptures-current (étape 3).
 * Gère 5 états : loading / live / stale / rate_limited / empty / unavailable
 * Gère 2 modes de matching : libelle_fuzzy / generic_fallback (incite import tickets).
 *
 * Tri natif déjà fait côté backend (criticité décroissante puis date).
 * Aucun spinner basique. Skeleton premium ivoire/bordeaux.
 */
export function AlertesRupturesSection({ onSwitchToImport }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("benchory_token");
      const resp = await axios.get(`${API_URL}/veille-proactive/ruptures-current`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });
      setPayload(resp.data);
      setError(null);
    } catch (e) {
      // On dégrade gracieusement même en cas d'erreur réseau : pas d'erreur brute
      // côté utilisateur, mais on log pour le support.
      // eslint-disable-next-line no-console
      console.warn("[veille-proactive] Ruptures fetch error:", e?.message);
      setError(e?.message || "Erreur réseau");
      setPayload({ data: [], meta: { status: "unavailable", user_message: null } });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section
      className="space-y-6"
      data-testid="alertes-ruptures-section"
      aria-labelledby="alertes-ruptures-title"
    >
      <SectionHeader
        title="Alertes ruptures personnalisées"
        subtitle="Les ruptures qui concernent vos références habituelles, croisées avec votre activité réelle"
        meta={payload?.meta}
        loading={loading}
        onRefresh={() => {
          setLoading(true);
          fetchData();
        }}
      />

      {loading && <RupturesSkeleton />}

      {!loading && payload && (
        <RupturesContent
          data={payload.data || []}
          meta={payload.meta || {}}
          onSwitchToImport={onSwitchToImport}
        />
      )}
    </section>
  );
}


// ── En-tête de section avec status badges ────────────────────────────────────


function SectionHeader({ title, subtitle, meta, loading, onRefresh }) {
  const status = meta?.status;
  const isStale = status === "stale" || meta?.cached_at && _isOld(meta.cached_at);
  const isRateLimited = status === "rate_limited";

  return (
    <header className="space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h2
            id="alertes-ruptures-title"
            className="font-playfair text-2xl lg:text-3xl text-cabinet-ink font-medium tracking-tight"
          >
            {title}
          </h2>
          <p className="mt-1.5 text-cabinet-navy text-[14px] leading-relaxed max-w-2xl">
            {subtitle}
          </p>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="flex-shrink-0 text-cabinet-mute-1 hover:text-cabinet-bordeaux transition-colors p-2 rounded-md disabled:opacity-40"
          data-testid="alertes-ruptures-refresh"
          aria-label="Actualiser les alertes ruptures"
        >
          <RefreshCw
            size={16}
            strokeWidth={1.8}
            className={loading ? "animate-spin" : ""}
          />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {isStale && (
          <StatusBadge
            icon={Clock}
            label={`Données du ${_formatDateTime(meta.cached_at)}`}
          />
        )}
        {isRateLimited && (
          <StatusBadge icon={Clock} label="Prochaine mise à jour dans quelques heures" />
        )}
        {meta?.criticality_distribution && _hasItems(meta.criticality_distribution) && (
          <DistributionBadges distribution={meta.criticality_distribution} />
        )}
      </div>
    </header>
  );
}


function StatusBadge({ icon: Icon, label }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cabinet-cream-warm border border-cabinet-mute-2/50 text-cabinet-mute-1 text-[11.5px] tracking-wide font-medium"
      data-testid="status-badge"
    >
      <Icon size={12} strokeWidth={1.8} /> {label}
    </span>
  );
}


function DistributionBadges({ distribution }) {
  const labels = {
    critical: { label: "critique", color: "bg-cabinet-bordeaux text-cabinet-cream" },
    high: { label: "élevée", color: "bg-cabinet-bordeaux/15 text-cabinet-bordeaux" },
    moderate: { label: "modérée", color: "bg-cabinet-navy/10 text-cabinet-navy" },
    info: { label: "information", color: "bg-cabinet-mute-1/10 text-cabinet-mute-1" },
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="distribution-badges">
      {Object.entries(distribution).map(([k, n]) =>
        n > 0 ? (
          <span
            key={k}
            className={`text-[11px] uppercase tracking-[0.12em] px-2 py-0.5 rounded font-semibold ${labels[k]?.color}`}
          >
            {n} {labels[k]?.label}
          </span>
        ) : null
      )}
    </div>
  );
}


// ── États (loading / live / empty / fallback / unavailable) ──────────────────


function RupturesSkeleton() {
  return (
    <div className="space-y-3" data-testid="ruptures-skeleton">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="bg-white border border-cabinet-mute-2/40 rounded-lg p-6 animate-pulse"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-cabinet-cream-warm" />
            <div className="flex-1 space-y-2.5">
              <div className="flex gap-2">
                <div className="h-3 w-14 rounded bg-cabinet-mute-2/50" />
                <div className="h-3 w-24 rounded bg-cabinet-mute-2/40" />
              </div>
              <div className="h-5 w-3/5 rounded bg-cabinet-mute-2/55" />
              <div className="h-3 w-4/5 rounded bg-cabinet-mute-2/30" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}


function RupturesContent({ data, meta, onSwitchToImport }) {
  const isGenericFallback = meta.matching_strategy === "generic_fallback";
  const isUnavailable = meta.status === "unavailable";
  const isEmpty = !data.length;

  if (isUnavailable) {
    return (
      <PremiumEmptyState
        icon={AlertTriangle}
        title="Les alertes ruptures sont momentanément indisponibles"
        message="Les analyses principales restent fiables. Notre équipe technique restaure la source dans les plus brefs délais."
        testId="ruptures-empty-unavailable"
      />
    );
  }

  if (isEmpty) {
    return (
      <PremiumEmptyState
        icon={Sparkles}
        title="Aucune rupture sur vos références habituelles"
        message="Profitez-en pour anticiper vos commandes saisonnières ou consultez les autres indicateurs de votre Veille Proactive."
        testId="ruptures-empty-no-alerts"
      />
    );
  }

  return (
    <div className="space-y-4">
      {isGenericFallback && (
        <GenericFallbackBanner
          message={meta.user_message}
          onSwitchToImport={onSwitchToImport}
        />
      )}
      <div className="space-y-3" data-testid="ruptures-list">
        {data.map((rupture) => (
          <RuptureCard
            key={rupture.id}
            rupture={rupture}
            matchingStrategy={meta.matching_strategy}
          />
        ))}
      </div>
    </div>
  );
}


function PremiumEmptyState({ icon: Icon, title, message, testId }) {
  return (
    <div
      className="bg-cabinet-cream-warm border border-cabinet-mute-2/40 rounded-lg px-8 py-12 text-center"
      data-testid={testId}
    >
      <div className="inline-flex w-12 h-12 rounded-full bg-cabinet-bordeaux/8 items-center justify-center mb-4">
        <Icon size={20} className="text-cabinet-bordeaux" strokeWidth={1.5} />
      </div>
      <h3 className="font-playfair text-xl text-cabinet-ink font-medium tracking-tight">
        {title}
      </h3>
      <p className="mt-2.5 text-cabinet-navy text-[14px] leading-relaxed max-w-md mx-auto">
        {message}
      </p>
    </div>
  );
}


function GenericFallbackBanner({ message, onSwitchToImport }) {
  return (
    <div
      className="bg-cabinet-cream-warm border border-cabinet-gold/40 rounded-lg px-6 py-5"
      data-testid="generic-fallback-banner"
    >
      <div className="flex items-start gap-3.5">
        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-cabinet-gold/15 flex items-center justify-center">
          <Upload size={16} className="text-cabinet-gold" strokeWidth={1.7} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-cabinet-ink text-[14px] leading-relaxed">
            {message ||
              "Importez vos tickets de caisse mensuels pour des alertes personnalisées sur vos références habituelles."}
          </p>
          {onSwitchToImport && (
            <button
              onClick={onSwitchToImport}
              className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-cabinet-bordeaux hover:text-cabinet-bordeaux-soft tracking-wide transition-colors"
              data-testid="generic-fallback-cta"
            >
              Importer mes tickets de caisse
              <span aria-hidden>→</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Helpers internes ──────────────────────────────────────────────────────────


function _formatDateTime(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const datePart = new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "long",
    }).format(d);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${datePart} à ${hh}h${mm}`;
  } catch {
    return iso;
  }
}


function _isOld(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return false;
    const ageHours = (Date.now() - d.getTime()) / (1000 * 60 * 60);
    return ageHours > 12;
  } catch {
    return false;
  }
}


function _hasItems(distribution) {
  return Object.values(distribution || {}).some((n) => n > 0);
}


export default AlertesRupturesSection;
